import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { useOps } from '../../../../../hooks/useOps';
import {
  formatCurrencyBrlFromCents,
  formatDateIso,
  formatPhone,
  formatCnpj,
  formatCpf,
} from '../../../../../utils';
import { ImileFechamentoOperacaoRow, ImileFechamentoResponse, ImileFechamentoResumoRow } from '../types';
import { romaneioService } from '../../../../../api';

export interface QuinzenaOption {
  inicio: string;
  fim: string;
  label: string;
  isCurrentQuinzena: boolean;
}

const MESES_PT = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

// Quinzena da iMile: 1 a 15 e 16 ao último dia do mês — mesma divisão do
// backend (imileFechamento) e dos vencimentos dia 6 / dia 16.
function quinzenaOption(ano: number, mes: number, quinzena: 1 | 2, isCurrentQuinzena: boolean): QuinzenaOption {
  const p = (n: number) => String(n).padStart(2, '0');
  // Dia 0 do mês seguinte = último dia deste mês
  const ultimoDia = new Date(ano, mes + 1, 0).getDate();
  const diaInicio = quinzena === 1 ? 1 : 16;
  const diaFim = quinzena === 1 ? 15 : ultimoDia;
  const inicio = `${ano}-${p(mes + 1)}-${p(diaInicio)}`;
  const fim = `${ano}-${p(mes + 1)}-${p(diaFim)}`;
  const nome = `${quinzena}ª quinzena de ${MESES_PT[mes]}/${ano}`;
  return {
    inicio,
    fim,
    label: isCurrentQuinzena ? `Quinzena atual — ${nome}` : nome,
    isCurrentQuinzena,
  };
}

export function generateQuinzenaOptions(numPastQuinzenas = 12, today = new Date()): QuinzenaOption[] {
  let ano = today.getFullYear();
  let mes = today.getMonth();
  let quinzena: 1 | 2 = today.getDate() <= 15 ? 1 : 2;

  const options: QuinzenaOption[] = [];
  for (let i = 0; i <= numPastQuinzenas; i++) {
    options.push(quinzenaOption(ano, mes, quinzena, i === 0));
    if (quinzena === 2) {
      quinzena = 1;
    } else {
      quinzena = 2;
      mes -= 1;
      if (mes < 0) {
        mes = 11;
        ano -= 1;
      }
    }
  }

  return options;
}

const imileFechamentoQueryKeys = {
  all: ['imile-fechamento'] as const,
  byOpsAndQuinzena: (ops: string, inicio: string) =>
    [...imileFechamentoQueryKeys.all, ops, inicio] as const,
};

async function fetchImileFechamento(
  ops: string,
  inicio: string,
  allowCurrentQuinzena: boolean
): Promise<ImileFechamentoResponse | null> {
  const response = await romaneioService.getImileFechamento({
    ops,
    inicio,
    ...(allowCurrentQuinzena ? { allowCurrentQuinzena: true } : {}),
  });
  if (response.success) {
    return (response.data as unknown as ImileFechamentoResponse) ?? null;
  }
  throw new Error(response.message || 'Erro ao carregar dados de fechamento iMile');
}

const downloadCsv = (rows: Record<string, unknown>[], filename: string) => {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      headers
        .map((h) => {
          const v = String(row[h] ?? '');
          return v.includes(',') || v.includes('"') || v.includes('\n')
            ? `"${v.replace(/"/g, '""')}"`
            : v;
        })
        .join(',')
    ),
  ].join('\n');

  const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const formatDoc = (doc: string): string => {
  const digits = doc.replace(/\D/g, '');
  if (digits.length === 11) return formatCpf(digits);
  if (digits.length === 14) return formatCnpj(digits);
  return doc;
};

export function useImileFechamento() {
  const { currentOps } = useOps();

  const quinzenaOptions = useMemo(() => generateQuinzenaOptions(12), []);
  const [selectedInicio, setSelectedInicio] = useState<string>(quinzenaOptions[1].inicio);

  const selectedQuinzenaOption =
    quinzenaOptions.find((q) => q.inicio === selectedInicio) ?? quinzenaOptions[1];
  const isCurrentQuinzena = selectedQuinzenaOption.isCurrentQuinzena;

  const enabled = Boolean(currentOps);

  const query = useQuery({
    queryKey: imileFechamentoQueryKeys.byOpsAndQuinzena(currentOps || '', selectedInicio),
    queryFn: () => fetchImileFechamento(currentOps!, selectedInicio, isCurrentQuinzena),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  const data = query.data ?? null;

  const buildFilename = useCallback(
    (prefix: string, ext: string) => {
      if (!data) return `${prefix}.${ext}`;
      const { quinzenaCompleta, periodo } = data;
      const quinzenaClosed = quinzenaCompleta && !isCurrentQuinzena;
      const suffix = quinzenaClosed ? '' : '_QUINZENA_NAO_FECHADA';
      return `${prefix}_${currentOps}_${periodo.inicio}_${periodo.fim}${suffix}.${ext}`;
    },
    [data, currentOps, isCurrentQuinzena]
  );

  const handleDownloadResumo = useCallback(() => {
    if (!data) return;
    const rows = data.resumo.map((r) => ({
      cnpj_cpf: r.cnpjCpf,
      nome_completo: r.nomeCompleto ?? '',
      cpf: r.cpf ?? '',
      telefone: r.telefone,
      valor_face_agregado: r.valorFaceAgregado,
    }));
    downloadCsv(rows as Record<string, unknown>[], buildFilename('resumo_imile', 'csv'));
  }, [data, buildFilename]);

  const handleDownloadOperacoes = useCallback(() => {
    if (!data) return;
    const rows = data.operacoes.map((r) => ({
      cnpj_cpf: r.cnpjCpf,
      nome_completo: r.nomeCompleto ?? '',
      cpf: r.cpf ?? '',
      cnpj: r.cnpj ?? '',
      telefone: r.telefone,
      valor_face: r.valorFace,
      valor_compra: r.valorCompra,
      data_romaneio: r.dataRomaneio?.substring(0, 10) ?? '',
      data_vencimento: r.dataVencimento?.substring(0, 10) ?? '',
      data_solicitado: r.dataSolicitado?.substring(0, 19) ?? '',
      comprovante_bancario: r.comprovanteBancario ?? '',
    }));
    downloadCsv(rows as Record<string, unknown>[], buildFilename('operacoes_imile', 'csv'));
  }, [data, buildFilename]);

  const summaryStats = useMemo(() => {
    if (!data) return null;
    const totalEntregadores = data.resumo.length;
    const totalOperacoes = data.operacoes.length;
    const valorFaceTotal = data.resumo.reduce((acc, r) => acc + r.valorFaceAgregado, 0);
    const comComprovante = data.operacoes.filter((o) => o.comprovanteBancario).length;
    return { totalEntregadores, totalOperacoes, valorFaceTotal, comComprovante };
  }, [data]);

  const resumoColumns: ColumnDef<ImileFechamentoResumoRow>[] = useMemo(
    () => [
      {
        accessorKey: 'cnpjCpf',
        header: 'Documento',
        size: 160,
        cell: ({ getValue }) => (
          <span className="font-mono text-sm">{formatDoc(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'nomeCompleto',
        header: 'Nome',
        size: 230,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="font-medium">{v}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)] italic text-sm">Não informado</span>
          );
        },
      },
      {
        accessorKey: 'cpf',
        header: 'CPF',
        size: 140,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="font-mono text-sm">{formatCpf(v.replace(/\D/g, ''))}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)]">—</span>
          );
        },
      },
      {
        accessorKey: 'telefone',
        header: 'Telefone',
        size: 150,
        cell: ({ getValue }) => (
          <span className="font-mono text-sm">{formatPhone(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'valorFaceAgregado',
        header: 'Valor Face Total',
        size: 160,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="font-semibold text-emerald-700">
            {formatCurrencyBrlFromCents(getValue() as number)}
          </span>
        ),
      },
    ],
    []
  );

  const operacoesColumns: ColumnDef<ImileFechamentoOperacaoRow>[] = useMemo(
    () => [
      {
        accessorKey: 'cnpjCpf',
        header: 'Documento',
        size: 160,
        cell: ({ getValue }) => (
          <span className="font-mono text-sm">{formatDoc(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'nomeCompleto',
        header: 'Nome',
        size: 200,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="font-medium">{v}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)] italic text-sm">Não informado</span>
          );
        },
      },
      {
        accessorKey: 'telefone',
        header: 'Telefone',
        size: 150,
        cell: ({ getValue }) => (
          <span className="font-mono text-sm">{formatPhone(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'valorFace',
        header: 'Valor Face',
        size: 130,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="font-semibold text-emerald-700">
            {formatCurrencyBrlFromCents(getValue() as number)}
          </span>
        ),
      },
      {
        accessorKey: 'valorCompra',
        header: 'Valor Compra',
        size: 130,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="font-semibold text-blue-700">
            {formatCurrencyBrlFromCents(getValue() as number)}
          </span>
        ),
      },
      {
        accessorKey: 'dataRomaneio',
        header: 'Romaneio',
        size: 120,
        cell: ({ getValue }) => (
          <span className="text-sm tabular-nums">{formatDateIso(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'dataVencimento',
        header: 'Vencimento',
        size: 120,
        cell: ({ getValue }) => (
          <span className="text-sm tabular-nums">{formatDateIso(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'dataSolicitado',
        header: 'Solicitado em',
        size: 140,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="text-sm tabular-nums">{formatDateIso(v)}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)]">—</span>
          );
        },
      },
      {
        accessorKey: 'comprovanteBancario',
        header: 'Comprovante',
        size: 150,
        cell: ({ getValue }) => {
          const url = getValue() as string | null;
          if (!url) {
            return <span className="text-[hsl(215,16%,70%)] text-sm">Sem comprovante</span>;
          }
          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[hsl(210,35%,32%)] hover:text-[hsl(210,35%,22%)] underline inline-flex items-center gap-1 text-sm font-medium"
            >
              Ver comprovante
              <ExternalLink className="h-3 w-3" />
            </a>
          );
        },
      },
    ],
    []
  );

  return {
    currentOps,
    data,
    summaryStats,
    loading: query.isLoading,
    isFetching: query.isFetching,
    error: query.isError ? query.error?.message || 'Erro ao conectar com o servidor' : '',
    resumoColumns,
    operacoesColumns,
    handleDownloadResumo,
    handleDownloadOperacoes,
    loadData: () => query.refetch(),
    quinzenaOptions,
    selectedInicio,
    setSelectedInicio,
    selectedQuinzenaOption,
    isCurrentQuinzena,
  };
}
